import {
  type Decoration,
  COLOUR2,
  blend,
  seeded,
  sparkle,
  tone,
} from "./effects";

/**
 * Konkord: the frames with a shape of their own (a serpent, a crown, cat ears,
 * runes...). All use the two chosen colours, stay near the edge, away from the
 * face, and repeat without a jump when `t` goes from 1 back to 0.
 */

export type FancyRing =
  | "comet"
  | "holo"
  | "plasma"
  | "equalizer"
  | "runes"
  | "gems"
  | "chain"
  | "hive"
  | "glitch"
  | "cat"
  | "crown"
  | "serpent"
  | "wings"
  | "constellation"
  | "ornate"
  | "vortex";

export const FANCY_RINGS: {
  id: FancyRing;
  label: string;
  animated: boolean;
}[] = [
  { id: "cat", label: "Gatinho", animated: true },
  { id: "crown", label: "Coroa", animated: true },
  { id: "serpent", label: "Serpente", animated: true },
  { id: "wings", label: "Asas", animated: true },
  { id: "comet", label: "Cometas", animated: true },
  { id: "holo", label: "Holográfica", animated: true },
  { id: "plasma", label: "Plasma", animated: true },
  { id: "equalizer", label: "Onda sonora", animated: true },
  { id: "runes", label: "Runas", animated: true },
  { id: "gems", label: "Joias", animated: true },
  { id: "chain", label: "Corrente", animated: true },
  { id: "hive", label: "Favo", animated: true },
  { id: "glitch", label: "Glitch", animated: true },
  { id: "constellation", label: "Constelação", animated: true },
  { id: "ornate", label: "Art déco", animated: true },
  { id: "vortex", label: "Vórtice", animated: true },
];

const TAU = Math.PI * 2;

export function isFancyRing(ring: string): ring is FancyRing {
  return FANCY_RINGS.some((fancy) => fancy.id === ring);
}

/**
 * @param k how thick the frame is (1 = the normal thickness)
 */
export function drawFancyRing(
  context: CanvasRenderingContext2D,
  size: number,
  t: number,
  decoration: Decoration,
  k: number,
) {
  const centre = size / 2;
  const colour = decoration.colour;
  const colour2 = decoration.colour2 ?? COLOUR2;
  const middle = blend(colour, colour2, 0.5);
  const at = (radius: number, angle: number): [number, number] => [
    centre + Math.cos(angle) * radius,
    centre + Math.sin(angle) * radius,
  ];
  const circle = (radius: number) => {
    context.beginPath();
    context.arc(centre, centre, radius, 0, TAU);
  };
  /** a fine line right on the edge */
  const edgeLine = (
    style: string | CanvasGradient,
    width = size * 0.012 * k,
  ) => {
    context.lineWidth = width;
    context.strokeStyle = style;
    circle(centre - width / 2);
    context.stroke();
  };

  context.save();
  context.lineCap = "round";
  context.lineJoin = "round";

  switch (decoration.ring) {
    case "comet": {
      const r = centre - size * 0.014 * k;
      edgeLine(tone(colour, 0, 0, 0.22), size * 0.006 * k);
      const comets: [number, string, number][] = [
        [0, colour, 1],
        [Math.PI, colour2, 0.75],
      ];
      for (const [offset, tint, weight] of comets) {
        const head = TAU * t + offset - Math.PI / 2;
        const tail = 2.2;
        const gradient = context.createConicGradient(
          head - tail,
          centre,
          centre,
        );
        gradient.addColorStop(0, tone(tint, 0, 0, 0));
        gradient.addColorStop(tail / TAU, tone(tint, 0, 12, 1));
        gradient.addColorStop(tail / TAU + 0.002, tone(tint, 0, 0, 0));
        gradient.addColorStop(1, tone(tint, 0, 0, 0));
        context.strokeStyle = gradient;
        context.lineWidth = size * 0.02 * k * weight;
        context.beginPath();
        context.arc(centre, centre, r, head - tail, head);
        context.stroke();
        const [x, y] = at(r, head);
        sparkle(context, x, y, size * 0.05 * weight, tint);
        // sparks left behind
        const random = seeded(61 + Math.round(offset));
        for (let i = 0; i < 6; i++) {
          const back = random() * tail;
          const [sx, sy] = at(
            r - size * (0.012 + random() * 0.03),
            head - back,
          );
          context.fillStyle = tone(tint, 0, 25, (1 - back / tail) * 0.9);
          context.beginPath();
          context.arc(sx, sy, size * 0.005, 0, TAU);
          context.fill();
        }
      }
      break;
    }

    case "holo": {
      const width = size * 0.018 * k;
      const band = (turn: number, alpha: number) => {
        const gradient = context.createConicGradient(turn, centre, centre);
        const stops = [
          colour,
          "#ffffff",
          colour2,
          tone(colour, 60, 15),
          "#ffffff",
          tone(colour2, -50, 15),
          colour,
        ];
        stops.forEach((stop, i) =>
          gradient.addColorStop(i / (stops.length - 1), stop),
        );
        context.globalAlpha = alpha;
        return gradient;
      };
      edgeLine(band(TAU * t, 1), width);
      context.lineWidth = size * 0.005 * k;
      context.strokeStyle = band(-TAU * t + 2, 0.8);
      circle(centre - width - size * 0.008);
      context.stroke();
      context.globalAlpha = 1;
      // light catching the surface
      for (let i = 0; i < 2; i++) {
        const [x, y] = at(centre - width / 2, TAU * t + i * Math.PI);
        sparkle(context, x, y, size * 0.035, i ? colour2 : colour);
      }
      break;
    }

    case "plasma": {
      const reach = size * 0.018;
      const r = centre - reach - size * 0.012 * k;
      const steps = 180;
      const strand = (phase: number, tint: string, width: number) => {
        context.beginPath();
        for (let i = 0; i <= steps; i++) {
          const angle = (i / steps) * TAU;
          const wave =
            Math.sin(5 * angle + TAU * (2 * t + phase)) * 0.65 +
            Math.sin(11 * angle - TAU * (3 * t - phase)) * 0.35;
          const [x, y] = at(r + wave * reach, angle);
          if (i) context.lineTo(x, y);
          else context.moveTo(x, y);
        }
        context.closePath();
        context.lineWidth = width;
        context.strokeStyle = tint;
        context.shadowColor = tint;
        context.shadowBlur = size * 0.022;
        context.stroke();
      };
      strand(0, colour, size * 0.01 * k);
      strand(0.5, colour2, size * 0.01 * k);
      context.shadowBlur = 0;
      strand(0, "rgba(255, 255, 255, 0.75)", size * 0.003 * k);
      context.shadowBlur = 0;
      break;
    }

    case "equalizer": {
      const bars = 54;
      const base = centre - size * 0.008;
      context.lineWidth = Math.max(size * 0.009 * k, size * 0.006);
      for (let i = 0; i < bars; i++) {
        const angle = (i / bars) * TAU - Math.PI / 2;
        const level =
          (0.5 + 0.5 * Math.sin(TAU * (2 * t + (i * 3) / bars))) *
          (0.45 + 0.55 * Math.abs(Math.sin(TAU * (t + (i * 7) / bars))));
        const length = size * (0.012 + 0.07 * level);
        const [x1, y1] = at(base, angle);
        const [x2, y2] = at(base - length, angle);
        // colour 1 at the top, colour 2 at the bottom, both sides alike
        const k2 = Math.abs(i / bars - 0.5) * 2;
        context.strokeStyle = tone(blend(colour2, colour, k2), 0, 8 * level);
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.stroke();
      }
      break;
    }

    case "runes": {
      const outer = centre - size * 0.006;
      const inner = centre - size * 0.085;
      context.lineWidth = size * 0.005 * k;
      context.strokeStyle = tone(colour, 0, 5, 0.9);
      circle(outer);
      context.stroke();
      circle(inner);
      context.stroke();
      // three different runes repeating: a quarter of a turn is a whole loop
      const glyphs: [number, number][][] = [
        [
          [-1, -1],
          [1, 1],
          [-1, 1],
          [1, -1],
        ],
        [
          [0, -1],
          [0, 1],
          [0, -0.2],
          [1, -1],
          [0, 0.2],
          [-1, 1],
        ],
        [
          [-1, 1],
          [0, -1],
          [0, -1],
          [1, 1],
          [-0.6, 0.2],
          [0.6, 0.2],
        ],
      ];
      const count = 12;
      const mid = (outer + inner) / 2;
      const unit = size * 0.021;
      context.lineWidth = size * 0.0065;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * TAU + (TAU * t) / 4;
        const light = 0.5 + 0.5 * Math.sin(TAU * (t + i / 3));
        const [x, y] = at(mid, angle);
        context.save();
        context.translate(x, y);
        context.rotate(angle + Math.PI / 2);
        context.strokeStyle = tone(
          blend(colour, colour2, light),
          0,
          10 + 15 * light,
        );
        context.shadowColor = colour2;
        context.shadowBlur = size * 0.012 * light;
        const lines = glyphs[i % 3];
        context.beginPath();
        for (let n = 0; n < lines.length; n += 2) {
          context.moveTo(lines[n][0] * unit, lines[n][1] * unit);
          context.lineTo(lines[n + 1][0] * unit, lines[n + 1][1] * unit);
        }
        context.stroke();
        context.restore();
      }
      break;
    }

    case "gems": {
      const r = centre - size * 0.03;
      edgeLine(tone(colour, 0, -10), size * 0.007 * k);
      context.lineWidth = size * 0.005;
      context.strokeStyle = tone(colour, 0, 15, 0.7);
      circle(r);
      context.stroke();
      const count = 12;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * TAU - Math.PI / 2;
        const tint = i % 2 ? colour2 : colour;
        const gem = size * (i % 2 ? 0.02 : 0.03) * Math.max(0.8, k);
        const [x, y] = at(r, angle);
        context.save();
        context.translate(x, y);
        context.rotate(angle + Math.PI / 4);
        const facet = context.createLinearGradient(-gem, -gem, gem, gem);
        facet.addColorStop(0, tone(tint, 0, 32));
        facet.addColorStop(0.45, tone(tint, 0, 2));
        facet.addColorStop(1, tone(tint, 0, -24));
        context.fillStyle = facet;
        context.fillRect(-gem * 0.8, -gem * 0.8, gem * 1.6, gem * 1.6);
        context.strokeStyle = "rgba(255, 255, 255, 0.55)";
        context.lineWidth = size * 0.002;
        context.strokeRect(-gem * 0.8, -gem * 0.8, gem * 1.6, gem * 1.6);
        context.restore();
        // the light runs from gem to gem
        let gap = Math.abs(i / count - t) % 1;
        gap = Math.min(gap, 1 - gap);
        const glow = Math.exp(-(gap * gap) / 0.003);
        if (glow > 0.05) sparkle(context, x, y, size * 0.05 * glow, tint);
      }
      break;
    }

    case "chain": {
      const count = 22;
      const r = centre - size * 0.024;
      // two links is one repeat, so two links per loop don't jump
      const shift = (2 * TAU * t) / count;
      const long = size * 0.026;
      for (let pass = 0; pass < 2; pass++) {
        for (let i = 0; i < count; i++) {
          if (i % 2 !== pass) continue;
          const angle = (i / count) * TAU + shift;
          const [x, y] = at(r, angle);
          const face = i % 2 === 0;
          const metal = context.createLinearGradient(
            x - long,
            y - long,
            x + long,
            y + long,
          );
          metal.addColorStop(0, tone(colour, 0, 32));
          metal.addColorStop(0.5, tone(colour, 0, -8));
          metal.addColorStop(1, tone(colour2, 0, 12));
          context.save();
          context.translate(x, y);
          context.rotate(angle + Math.PI / 2);
          context.strokeStyle = metal;
          context.lineWidth = size * 0.0085 * Math.max(0.75, k);
          context.beginPath();
          context.ellipse(
            0,
            0,
            long,
            face ? size * 0.012 : size * 0.0025,
            0,
            0,
            TAU,
          );
          context.stroke();
          context.restore();
        }
      }
      break;
    }

    case "hive": {
      const count = 22;
      const r = centre - size * 0.042;
      const cell = size * 0.033;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * TAU;
        const offset = i % 2 ? size * 0.016 : -size * 0.008;
        const [x, y] = at(r + offset, angle);
        let gap = Math.abs(i / count - t) % 1;
        gap = Math.min(gap, 1 - gap);
        const light = Math.exp(-(gap * gap) / 0.012);
        context.beginPath();
        for (let side = 0; side < 6; side++) {
          const a = angle + (side / 6) * TAU;
          const px = x + Math.cos(a) * cell;
          const py = y + Math.sin(a) * cell;
          if (side) context.lineTo(px, py);
          else context.moveTo(px, py);
        }
        context.closePath();
        context.fillStyle = tone(
          blend(colour, colour2, light),
          0,
          8 + 15 * light,
          0.14 + 0.8 * light,
        );
        context.fill();
        context.lineWidth = size * 0.0045 * Math.max(0.8, k);
        context.strokeStyle = tone(colour, 0, 12, 0.95);
        context.stroke();
      }
      break;
    }

    case "glitch": {
      const r = centre - size * 0.016;
      const width = size * 0.012 * k;
      // the same 10 broken moments on every loop
      const moment = Math.floor(t * 10) % 10;
      const random = seeded(900 + moment);
      const quiet = moment % 4 === 3;
      const split = quiet ? size * 0.002 : size * 0.008;
      const dx = (random() - 0.5) * split * 2;
      const dy = (random() - 0.5) * split * 2;
      context.globalCompositeOperation = "lighter";
      context.lineCap = "butt";
      context.lineWidth = width;
      const part = (x: number, y: number, tint: string) => {
        context.strokeStyle = tint;
        context.beginPath();
        context.arc(centre + x, centre + y, r, 0, TAU);
        context.stroke();
      };
      part(dx, dy, tone(colour, 0, 5));
      part(-dx, -dy, tone(colour2, 0, 5));
      context.globalCompositeOperation = "source-over";
      // pieces of the frame thrown out of place
      const pieces = quiet ? 1 : 5;
      for (let i = 0; i < pieces; i++) {
        const from = random() * TAU;
        const length = 0.12 + random() * 0.32;
        const jump = size * (0.012 + random() * 0.03);
        context.lineWidth = width * (0.8 + random());
        context.strokeStyle = i % 2 ? colour : colour2;
        context.beginPath();
        context.arc(centre, centre, r - jump, from, from + length);
        context.stroke();
        context.strokeStyle = "#ffffff";
        context.lineWidth = size * 0.003;
        context.beginPath();
        context.arc(centre, centre, r - jump, from, from + length * 0.5);
        context.stroke();
      }
      break;
    }

    case "cat": {
      edgeLine(colour, size * 0.012 * k);
      const twitch = Math.max(0, Math.sin(TAU * t)) ** 6;
      const ear = (side: -1 | 1) => {
        // pointed, standing on the head: the tip touches the edge
        const tip =
          -Math.PI / 2 + side * (0.62 + (side === 1 ? twitch * 0.05 : 0));
        const shape = (reachIn: number, spread: number, tipAt: number) => {
          const [tx, ty] = at(centre * tipAt, tip);
          const [ax, ay] = at(centre * reachIn, tip - spread);
          const [bx, by] = at(centre * reachIn, tip + spread);
          const [cx, cy] = at(centre * (reachIn - 0.03), tip);
          context.beginPath();
          context.moveTo(ax, ay);
          context.lineTo(tx, ty);
          context.lineTo(bx, by);
          context.quadraticCurveTo(cx, cy, ax, ay);
          context.closePath();
        };
        context.lineWidth = size * 0.012;
        context.fillStyle = colour;
        context.strokeStyle = colour;
        shape(0.7, 0.34, 0.985);
        context.fill();
        context.stroke();
        context.fillStyle = colour2;
        shape(0.78, 0.17, 0.92);
        context.fill();
      };
      ear(-1);
      ear(1);
      // a tail along the frame, swaying
      const sway = Math.sin(TAU * t);
      context.strokeStyle = colour;
      context.lineWidth = size * 0.024;
      const start = at(centre - size * 0.014, Math.PI * 0.28);
      const middleTail = at(
        centre - size * 0.03 - sway * size * 0.01,
        Math.PI * 0.4,
      );
      const endTail = at(
        centre - size * 0.075 - sway * size * 0.03,
        Math.PI * (0.52 + sway * 0.015),
      );
      context.beginPath();
      context.moveTo(start[0], start[1]);
      context.quadraticCurveTo(
        middleTail[0],
        middleTail[1],
        endTail[0],
        endTail[1],
      );
      context.stroke();
      context.fillStyle = colour2;
      context.beginPath();
      context.arc(endTail[0], endTail[1], size * 0.017, 0, TAU);
      context.fill();
      break;
    }

    case "crown": {
      const metal = context.createLinearGradient(0, 0, size, size);
      metal.addColorStop(0, tone(colour, 0, 25));
      metal.addColorStop(0.45, tone(colour, 0, -12));
      metal.addColorStop(1, tone(colour, 0, 15));
      edgeLine(metal, size * 0.014 * k);
      // the crown, leaning a little, on the top of the frame
      const w = size * 0.23;
      const h = size * 0.17;
      const [cx, cy] = at(centre * 0.61, -Math.PI / 2 - 0.34);
      context.save();
      context.translate(cx, cy);
      context.rotate(-0.34);
      const body = context.createLinearGradient(0, -h, 0, h * 0.2);
      body.addColorStop(0, tone(colour, 0, 30));
      body.addColorStop(0.7, tone(colour, 0, -4));
      body.addColorStop(1, tone(colour, 0, -24));
      context.fillStyle = body;
      context.strokeStyle = tone(colour, 0, -32);
      context.lineWidth = size * 0.004;
      context.beginPath();
      context.moveTo(-w / 2, 0);
      context.lineTo(-w / 2 - w * 0.08, -h * 0.72);
      context.lineTo(-w * 0.22, -h * 0.32);
      context.lineTo(0, -h);
      context.lineTo(w * 0.22, -h * 0.32);
      context.lineTo(w / 2 + w * 0.08, -h * 0.72);
      context.lineTo(w / 2, 0);
      context.closePath();
      context.fill();
      context.stroke();
      // the stones and the points' balls
      const stones: [number, number, string][] = [
        [-w * 0.27, -h * 0.13, colour2],
        [0, -h * 0.17, tone(colour2, 40, 5)],
        [w * 0.27, -h * 0.13, colour2],
      ];
      for (const [x, y, tint] of stones) {
        context.fillStyle = tint;
        context.beginPath();
        context.arc(x, y, size * 0.011, 0, TAU);
        context.fill();
      }
      const tips: [number, number][] = [
        [-w / 2 - w * 0.08, -h * 0.72],
        [0, -h],
        [w / 2 + w * 0.08, -h * 0.72],
      ];
      tips.forEach(([x, y]) => {
        context.fillStyle = tone(colour, 0, 32);
        context.beginPath();
        context.arc(x, y, size * 0.0085, 0, TAU);
        context.fill();
      });
      tips.forEach(([x, y], i) => {
        const glow = Math.max(0, Math.sin(TAU * (t + i / 3))) ** 8;
        sparkle(context, x, y, size * 0.05 * glow, colour2);
      });
      context.restore();
      // a glint around the frame
      const [gx, gy] = at(centre - size * 0.008, TAU * t);
      sparkle(context, gx, gy, size * 0.03, colour);
      break;
    }

    case "serpent": {
      const r = centre - size * 0.034;
      const segments = 96;
      const length = TAU * 0.86;
      const head = TAU * t - Math.PI / 2;
      const thick = size * 0.02 * Math.max(0.8, k);
      for (let i = 0; i <= segments; i++) {
        const along = i / segments;
        const angle = head - length * (1 - along);
        // it slithers: a whole number of waves along the body and the loop
        const wave = Math.sin(TAU * (along * 7 - t * 3));
        const [x, y] = at(r + wave * size * 0.012, angle);
        const taper = Math.min(1, along * 3.2) * (0.72 + 0.28 * along);
        const stripe = Math.floor(along * 26) % 2 === 0;
        context.fillStyle = stripe
          ? tone(colour, 0, -2 + along * 10)
          : tone(colour2, 0, along * 12);
        context.beginPath();
        context.arc(x, y, thick * taper, 0, TAU);
        context.fill();
      }
      // the head, with its eyes and its tongue
      const wave = Math.sin(TAU * (7 - t * 3));
      const [hx, hy] = at(r + wave * size * 0.012, head);
      context.save();
      context.translate(hx, hy);
      context.rotate(head + Math.PI / 2);
      context.fillStyle = tone(colour, 0, 8);
      context.beginPath();
      context.ellipse(size * 0.012, 0, size * 0.036, size * 0.026, 0, 0, TAU);
      context.fill();
      context.fillStyle = "#ffffff";
      for (const side of [-1, 1]) {
        context.beginPath();
        context.arc(size * 0.02, side * size * 0.014, size * 0.0085, 0, TAU);
        context.fill();
      }
      context.fillStyle = "#111111";
      for (const side of [-1, 1]) {
        context.beginPath();
        context.arc(size * 0.023, side * size * 0.014, size * 0.0042, 0, TAU);
        context.fill();
      }
      const lick = Math.max(0, Math.sin(TAU * 4 * t));
      if (lick > 0.05) {
        context.strokeStyle = "#f43f5e";
        context.lineWidth = size * 0.004;
        const out = size * (0.05 + 0.025 * lick);
        context.beginPath();
        context.moveTo(size * 0.044, 0);
        context.lineTo(out, 0);
        context.moveTo(out, 0);
        context.lineTo(out + size * 0.012, -size * 0.008);
        context.moveTo(out, 0);
        context.lineTo(out + size * 0.012, size * 0.008);
        context.stroke();
      }
      context.restore();
      break;
    }

    case "wings": {
      edgeLine(tone(colour, 0, 8, 0.85), size * 0.008 * k);
      const flap = Math.sin(TAU * t);
      for (const side of [-1, 1] as const) {
        const layers: [number, number, number, string, string][] = [
          // how many feathers, how long, how far in, base and tip
          [8, 0.25, 0.8, tone(colour, 0, 12), tone(colour2, 0, 14, 0.95)],
          [7, 0.14, 0.86, "#ffffff", tone(colour, 0, 20)],
        ];
        for (const [count, long, inset, base, tip] of layers) {
          for (let i = count - 1; i >= 0; i--) {
            const k2 = i / (count - 1);
            // the shoulder high on the side, the feathers fanning down
            const spot = -0.72 + k2 * 1.25;
            const angle = side === 1 ? spot : Math.PI - spot;
            const [x, y] = at(centre * inset, angle);
            const length = size * long * (1 - k2 * 0.4);
            const open = 0.2 + k2 * 0.45 + flap * 0.1 * (1 - k2);
            context.save();
            context.translate(x, y);
            // along the edge, leaning towards the middle at the far end
            context.rotate(
              angle + (side === 1 ? Math.PI / 2 + open : -Math.PI / 2 - open),
            );
            const feather = context.createLinearGradient(0, 0, length, 0);
            feather.addColorStop(0, base);
            feather.addColorStop(1, tip);
            context.fillStyle = feather;
            context.strokeStyle = "rgba(0, 0, 0, 0.22)";
            context.lineWidth = size * 0.002;
            context.beginPath();
            context.moveTo(0, 0);
            context.quadraticCurveTo(length * 0.45, -length * 0.2, length, 0);
            context.quadraticCurveTo(length * 0.45, length * 0.16, 0, 0);
            context.fill();
            context.stroke();
            context.restore();
          }
        }
      }
      break;
    }

    case "constellation": {
      edgeLine(tone(colour, 0, 0, 0.3), size * 0.005 * k);
      const random = seeded(77);
      const count = 15;
      const stars = Array.from({ length: count }, (_, i) => {
        const angle = ((i + random() * 0.6) / count) * TAU;
        const radius = centre * (0.77 + random() * 0.19);
        return { angle, radius, phase: random(), big: random() > 0.6 };
      });
      context.lineWidth = size * 0.0035;
      context.strokeStyle = tone(colour, 0, 15, 0.55);
      context.beginPath();
      stars.forEach((star, i) => {
        const [x, y] = at(star.radius, star.angle);
        if (i % 5 === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      });
      context.stroke();
      stars.forEach((star, i) => {
        const [x, y] = at(star.radius, star.angle);
        const glow = 0.5 + 0.5 * Math.sin(TAU * (t + star.phase));
        if (star.big)
          sparkle(
            context,
            x,
            y,
            size * (0.025 + 0.025 * glow),
            i % 2 ? colour : colour2,
          );
        else {
          context.fillStyle = tone(
            i % 2 ? colour : colour2,
            0,
            25,
            0.6 + 0.4 * glow,
          );
          context.beginPath();
          context.arc(x, y, size * 0.0075, 0, TAU);
          context.fill();
        }
      });
      // a crescent moon
      const [mx, my] = at(centre * 0.84, -Math.PI * 0.78);
      const moon = size * 0.042;
      context.fillStyle = tone(colour2, 0, 25);
      context.beginPath();
      context.arc(mx, my, moon, 0.5, TAU - 0.5);
      context.arc(mx + moon * 0.55, my, moon * 0.8, TAU - 0.75, 0.75, true);
      context.closePath();
      context.fill();
      // a shooting star crossing the edge once per loop
      if (t < 0.25) {
        const go = t / 0.25;
        const angle = 0.5 + go * 1.1;
        const [x, y] = at(centre * 0.88, angle);
        const [bx, by] = at(centre * 0.9, angle - 0.32);
        const trail = context.createLinearGradient(bx, by, x, y);
        trail.addColorStop(0, "rgba(255, 255, 255, 0)");
        trail.addColorStop(1, `rgba(255, 255, 255, ${Math.sin(Math.PI * go)})`);
        context.strokeStyle = trail;
        context.lineWidth = size * 0.006;
        context.beginPath();
        context.moveTo(bx, by);
        context.lineTo(x, y);
        context.stroke();
      }
      break;
    }

    case "ornate": {
      const outer = size * 0.008 * k;
      const metal = context.createConicGradient(TAU * t, centre, centre);
      [
        colour,
        tone(colour, 0, 30),
        colour2,
        tone(colour2, 0, 25),
        colour,
      ].forEach((stop, i) => metal.addColorStop(i / 4, stop));
      edgeLine(metal, outer);
      const inner = centre - size * 0.07;
      context.lineWidth = size * 0.004;
      context.strokeStyle = metal;
      circle(inner);
      context.stroke();
      const count = 24;
      const r = centre - outer;
      const step = TAU / count;
      for (let i = 0; i < count; i++) {
        const angle = i * step;
        // a fan between the two lines
        const [cx, cy] = at(inner, angle + step / 2);
        const reach = centre - size * 0.02 - inner;
        context.lineWidth = size * 0.0035;
        context.strokeStyle = metal;
        for (const scale of [1, 0.55]) {
          context.beginPath();
          context.arc(
            cx,
            cy,
            reach * scale,
            angle + step / 2 - Math.PI / 2,
            angle + step / 2 + Math.PI / 2,
          );
          context.stroke();
        }
        const [dx, dy] = at(r - size * 0.012, angle);
        let gap = Math.abs(i / count - t) % 1;
        gap = Math.min(gap, 1 - gap);
        const glow = Math.exp(-(gap * gap) / 0.004);
        context.fillStyle = tone(colour2, 0, 18 + 15 * glow);
        context.beginPath();
        context.arc(dx, dy, size * (0.006 + 0.005 * glow), 0, TAU);
        context.fill();
        if (glow > 0.3) sparkle(context, dx, dy, size * 0.04 * glow, colour);
      }
      break;
    }

    case "vortex": {
      const arms = 12;
      // two arms is one repeat (the colours alternate)
      const spin = (2 * TAU * t) / arms;
      const outer = centre - size * 0.004;
      const inner = centre * 0.74;
      for (let i = 0; i < arms; i++) {
        const start = (i / arms) * TAU + spin;
        const tint = i % 2 ? colour2 : colour;
        const steps = 16;
        for (let s = 0; s < steps; s++) {
          const from = s / steps;
          const to = (s + 1) / steps;
          const point = (k2: number) =>
            at(outer + (inner - outer) * k2, start + k2 * 0.95);
          const [x1, y1] = point(from);
          const [x2, y2] = point(to);
          context.strokeStyle = tone(tint, 0, 12, (1 - from) ** 1.4);
          context.lineWidth = size * 0.015 * k * (1 - from * 0.8);
          context.beginPath();
          context.moveTo(x1, y1);
          context.lineTo(x2, y2);
          context.stroke();
        }
      }
      edgeLine(tone(middle, 0, 15), size * 0.006 * k);
      break;
    }
  }
  context.restore();
}
